import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
      dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "retail-ecommerce-solution" },
      { name: "Real Estate", link: "/realestate-solution" },
      { name: "Logistics", link: "/logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];


// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🤖",
    title: "Native iOS Development",
    desc: "Blazing-fast, pixel-perfect iOS apps built with Swift & SwiftUI — designed to delight Apple users and sail through App Store review.",
    tags: ["Swift", "SwiftUI", "Xcode", "TestFlight"],
    color: "#3B82F6",
  },
  {
    icon: "🤖",
    title: "Native Android Development",
    desc: "High-performance Android apps in Kotlin, following Material Design 3 guidelines, optimised for the full range of Android devices.",
    tags: ["Kotlin", "Jetpack Compose", "Android Studio", "Play Store"],
    color: "#10B981",
  },
  {
    icon: "📱",
    title: "Cross-Platform (Flutter)",
    desc: "One codebase, two stunning apps. Flutter lets us ship iOS and Android simultaneously without sacrificing native look, feel, or performance.",
    tags: ["Flutter", "Dart", "Firebase", "GetX"],
    color: "#8B5CF6",
  },
  {
    icon: "⚛️",
    title: "React Native Apps",
    desc: "JavaScript-powered cross-platform apps that share logic with your existing web stack — perfect for teams who want rapid iteration.",
    tags: ["React Native", "Expo", "Redux", "TypeScript"],
    color: "#F59E0B",
  },
  {
    icon: "🛒",
    title: "M-Commerce Apps",
    desc: "Revenue-generating shopping apps with AR try-on, one-tap checkout, push-notification campaigns, and real-time inventory sync.",
    tags: ["Flutter", "Stripe", "Firebase", "AR Kit"],
    color: "#EF4444",
  },
  {
    icon: "🏥",
    title: "Healthcare & Fitness Apps",
    desc: "HIPAA-compliant telemedicine, health tracking, and fitness coaching apps with HealthKit, Google Fit, and wearable integrations.",
    tags: ["HealthKit", "Google Fit", "HIPAA", "WebRTC"],
    color: "#EC4899",
  },
  {
    icon: "💳",
    title: "FinTech Mobile Apps",
    desc: "Secure digital wallets, payment apps, and investment platforms with biometric authentication, real-time notifications, and PCI DSS compliance.",
    tags: ["Biometrics", "Plaid", "Stripe", "PCI DSS"],
    color: "#06B6D4",
  },
  {
    icon: "🗺️",
    title: "On-Demand & Location Apps",
    desc: "Uber-style on-demand platforms, delivery tracking, and geo-fencing apps with real-time maps, driver matching, and surge pricing engines.",
    tags: ["Google Maps", "WebSockets", "Node.js", "Redis"],
    color: "#6366F1",
  },
];

const TECH_STACK = [
  {
    category: "iOS",
    items: [
      { name: "Swift", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/swift/swift-original.svg", color: "#FA7343" },
      { name: "SwiftUI", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/swift/swift-original.svg", color: "#0070C9" },
      { name: "Xcode", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/xcode/xcode-original.svg", color: "#1575F9" },
      { name: "Objective-C", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/objectivec/objectivec-plain.svg", color: "#0071C5" },
      { name: "Core Data", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/apple/apple-original.svg", color: "#555" },
      { name: "TestFlight", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/apple/apple-original.svg", color: "#0071C5" },
    ],
  },
  {
    category: "Android",
    items: [
      { name: "Kotlin", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kotlin/kotlin-original.svg", color: "#7F52FF" },
      { name: "Android", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/android/android-original.svg", color: "#3DDC84" },
      { name: "Java", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original.svg", color: "#007396" },
      { name: "Jetpack", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/android/android-original.svg", color: "#4CAF50" },
      { name: "Gradle", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/gradle/gradle-original.svg", color: "#02303A" },
      { name: "Android Studio", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/androidstudio/androidstudio-original.svg", color: "#3DDC84" },
    ],
  },
  {
    category: "Cross-Platform",
    items: [
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg", color: "#02569B" },
      { name: "React Native", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Dart", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/dart/dart-original.svg", color: "#0175C2" },
      { name: "Expo", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#000020" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Redux", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redux/redux-original.svg", color: "#764ABC" },
    ],
  },
  {
    category: "Backend & Cloud",
    items: [
      { name: "Firebase", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/firebase/firebase-plain.svg", color: "#FFCA28" },
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/graphql/graphql-plain.svg", color: "#E10098" },
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Strategy & Discovery", desc: "We map your users' journey, define core features for the MVP, and produce a prioritised product backlog and project charter.", color: "#3B82F6" },
  { n: "02", icon: "🎨", title: "UI/UX & Prototyping", desc: "Platform-native wireframes evolve into interactive Figma prototypes — tested with real users before any code is written.", color: "#8B5CF6" },
  { n: "03", icon: "⚙️", title: "Agile Development", desc: "Two-week sprints with a working app build after every sprint. You see progress, not promises.", color: "#10B981" },
  { n: "04", icon: "🧪", title: "QA & Device Testing", desc: "Automated unit & UI tests plus manual testing across 20+ real iOS/Android devices, OS versions, and screen sizes.", color: "#F59E0B" },
  { n: "05", icon: "🚀", title: "Store Submission", desc: "We handle App Store & Play Store submissions end-to-end — metadata, screenshots, compliance review, and go-live monitoring.", color: "#EF4444" },
  { n: "06", icon: "📈", title: "Maintenance & Growth", desc: "Post-launch crash monitoring, OS update compatibility, A/B testing, and feature iterations driven by real user analytics.", color: "#EC4899" },
];

const CASE_STUDIES = [
  {
    title: "FinEdge Mobile",
    industry: "FinTech",
    desc: "A next-gen financial super-app enabling seamless UPI payments, micro-investments, and AI-powered budgeting for 50,000+ daily active users across iOS and Android.",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop&auto=format",
    tags: ["Flutter", "Firebase", "Python", "ML"],
    metrics: [{ val: "50k+", lbl: "Daily Active Users" }, { val: "4.8★", lbl: "App Store Rating" }, { val: "₹2Cr+", lbl: "Daily Transactions" }],
    color: "#8B5CF6",
  },
  {
    title: "TravelAmour App",
    industry: "Travel",
    desc: "An AI-powered travel companion with personalised itinerary generation, community reviews, and instant booking for flights, hotels, and experiences in 50+ countries.",
    img: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&h=400&fit=crop&auto=format",
    tags: ["React Native", "GraphQL", "MongoDB", "GCP"],
    metrics: [{ val: "500k+", lbl: "Trips Planned" }, { val: "4.9★", lbl: "Play Store Rating" }, { val: "50+", lbl: "Countries" }],
    color: "#06B6D4",
  },
  {
    title: "iClinix Telemedicine",
    industry: "Healthcare",
    desc: "A HIPAA-compliant telemedicine app for 300+ clinics featuring HD video consultations, e-prescriptions, appointment scheduling, and integrated lab report delivery.",
    img: "https://images.unsplash.com/photo-1576671081837-49000212a370?w=600&h=400&fit=crop&auto=format",
    tags: ["Flutter", "WebRTC", "Node.js", "AWS"],
    metrics: [{ val: "300+", lbl: "Clinics Onboarded" }, { val: "99.9%", lbl: "Call Uptime" }, { val: "HIPAA", lbl: "Compliant" }],
    color: "#3B82F6",
  },
];

const FAQS = [
  { q: "How long does it take to build a mobile app?", a: "A simple MVP app with 5–8 screens typically takes 6–10 weeks. A mid-complexity app (social, e-commerce) takes 3–5 months. A full-featured platform with backend APIs, admin panels, and integrations can take 5–9 months. We provide a detailed milestone plan in the discovery phase." },
  { q: "Should I build native iOS/Android or cross-platform?", a: "It depends on your goals. Native (Swift/Kotlin) gives maximum performance and deepest platform integration — ideal for games, AR, or hardware-heavy apps. Cross-platform (Flutter/React Native) gives you one codebase for both platforms at 70–80% of native performance — ideal for most business apps. We'll recommend the right approach after understanding your use case." },
  { q: "Do you handle App Store and Play Store submission?", a: "Yes, completely. We manage App Store Connect and Google Play Console setup, prepare all metadata, screenshots, privacy policies, and compliance documentation, and handle the review process — including responding to any reviewer feedback until your app goes live." },
  { q: "Will my app work offline?", a: "Absolutely. We design offline-first architectures using local databases (SQLite, Hive, Core Data) and intelligent sync strategies so your app remains functional even without internet connectivity — a must for logistics, healthcare, and field service apps." },
  { q: "How do you ensure app security?", a: "We implement certificate pinning, encrypted local storage, biometric authentication, obfuscated code, and secure API communication. For FinTech and Healthcare apps we additionally apply PCI DSS and HIPAA controls and conduct third-party penetration testing before launch." },
  { q: "What post-launch support do you offer?", a: "All projects include 30 days free post-launch bug fixes. Beyond that, we offer monthly maintenance retainers covering crash monitoring (Crashlytics/Sentry), OS compatibility updates (Apple and Google release updates annually), performance tuning, and new feature sprints." },
];

const STATS = [
  { val: "480+", lbl: "Apps Shipped", icon: "📱" },
  { val: "20+", lbl: "Platforms & Devices", icon: "🌐" },
  { val: "4.8★", lbl: "Avg Store Rating", icon: "⭐" },
  { val: "0", lbl: "App Store Rejections", icon: "🏆" },
];

const PLATFORMS = [
  { name: "iOS App Store", icon: "🍎", color: "#000", bg: "#F5F5F7", count: "240+ apps" },
  { name: "Google Play", icon: "🤖", color: "#01875F", bg: "#F0FDF4", count: "280+ apps" },
  { name: "Flutter", icon: "💙", color: "#02569B", bg: "#EFF6FF", count: "120+ apps" },
  { name: "React Native", icon: "⚛️", color: "#61DAFB", bg: "#F0FDFF", count: "90+ apps" },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Smart logic to check which page we are currently on
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            // Check if this specific main link is the active page
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());

            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    // Highlight the "Services" button if we are on the web-development page
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        // Highlight the active item inside the dropdown
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="/#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
          }}>☰</button>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ paddingBottom: 14, borderBottom: "1px solid #F3F4F6", marginBottom: 4 }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          {NAV_LINKS.map(l => (
            <a key={l.label} href={l.link || "/#services"} style={{ padding: "10px 0", color: currentPath.includes(l.label.toLowerCase()) ? "#3B82F6" : "#374151", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #F3F4F6", fontFamily: "'Sora',sans-serif" }}>{l.label}</a>
          ))}
          <a href="/#contact" style={{ marginTop: 12, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", padding: "13px", borderRadius: 8, textAlign: "center", fontWeight: 700, textDecoration: "none" }}>Get Free Consultation</a>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [activePhone, setActivePhone] = useState(0);
  const phones = ["📱", "🤖", "💙"];

  useEffect(() => {
    const t = setInterval(() => setActivePhone(p => (p + 1) % phones.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 96,
      background: "linear-gradient(160deg,#F5F3FF 0%,#EFF6FF 50%,#F0FDF4 100%)",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -100, right: -100, width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(139,92,246,.1),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -80, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(59,130,246,.08),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(139,92,246,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(139,92,246,.04) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#6B7280" }}>
          <Link to="/" style={{ color: "#8B5CF6", textDecoration: "none", fontWeight: 600 }}>Home</Link>
          <span>›</span>
          <span>Services</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600 }}>Mobile App Development</span>
        </div>

        <div className="mad-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 99, padding: "6px 16px", marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#8B5CF6", display: "inline-block", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#5B21B6" }}>📱 India's Top Mobile App Development Company</span>
            </div>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(34px,4.5vw,58px)", fontWeight: 900, color: "#0F172A", lineHeight: 1.1, marginBottom: 20 }}>
              Mobile Apps That<br />
              <span style={{ background: "linear-gradient(135deg,#6D28D9,#8B5CF6,#06B6D4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Users Love & Keep
              </span>
            </h1>
            <p style={{ fontSize: 17, color: "#6B7280", lineHeight: 1.8, maxWidth: 560, marginBottom: 36 }}>
              We craft stunning iOS and Android apps — from lean MVPs to enterprise-scale platforms — that average 4.8★ on the app stores and turn first-time users into loyal advocates.
            </p>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 44 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#6D28D9,#8B5CF6)", color: "#fff",
                padding: "15px 30px", borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 6px 20px rgba(139,92,246,.4)",
                transition: "transform .2s,box-shadow .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 28px rgba(139,92,246,.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(139,92,246,.4)"; }}
              >🚀 Build My App</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#6D28D9", padding: "15px 30px", borderRadius: 10,
                fontWeight: 700, fontSize: 15, textDecoration: "none", border: "2px solid #DDD6FE",
                transition: "all .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#F5F3FF"; e.currentTarget.style.borderColor = "#C4B5FD"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#DDD6FE"; }}
              >📂 See Our Apps</a>
            </div>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {[["480+", "Apps Shipped"], ["4.8★", "Avg Rating"], ["0", "Store Rejections"], ["20+", "Platforms"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#6D28D9" }}>{v}</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 600 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Phone mockup */}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", position: "relative" }}>
            {/* Glow */}
            <div style={{ position: "absolute", width: 340, height: 340, borderRadius: "50%", background: "radial-gradient(circle,rgba(139,92,246,.15),transparent 70%)", pointerEvents: "none" }} />

            {/* Phone frame */}
            <div style={{
              width: 280, height: 560,
              background: "#0F172A",
              borderRadius: 44,
              padding: 10,
              boxShadow: "0 40px 100px rgba(139,92,246,.25), 0 0 0 1px rgba(255,255,255,.1)",
              position: "relative", zIndex: 2,
              animation: "float 4s ease-in-out infinite",
            }}>
              {/* Notch */}
              <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", width: 100, height: 28, background: "#0F172A", borderRadius: 20, zIndex: 10 }} />
              {/* Screen */}
              <div style={{ width: "100%", height: "100%", borderRadius: 36, overflow: "hidden", background: "#fff", position: "relative" }}>
                <img
                  src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=700&fit=crop&auto=format"
                  alt="App Screen"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {/* Overlay gradient */}
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 40%, rgba(109,40,217,.6) 100%)" }} />
                {/* Bottom label */}
                <div style={{ position: "absolute", bottom: 28, left: 20, right: 20 }}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#fff", marginBottom: 4 }}>Your App Here</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)" }}>Designed & built by Techvanto</div>
                  <div style={{ display: "flex", gap: 3, marginTop: 8 }}>
                    {[1,2,3,4,5].map(i => <span key={i} style={{ color: "#F59E0B", fontSize: 12 }}>★</span>)}
                    <span style={{ color: "rgba(255,255,255,.7)", fontSize: 11, marginLeft: 4 }}>4.9 (2.4k reviews)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating cards */}
            <div style={{ position: "absolute", top: 30, left: -10, background: "#fff", borderRadius: 16, padding: "12px 16px", boxShadow: "0 12px 40px rgba(139,92,246,.2)", border: "1.5px solid #DDD6FE", animation: "float 3s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, color: "#6D28D9" }}>4.9 ★★★★★</div>
              <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500, marginTop: 2 }}>App Store Rating 🍎</div>
            </div>
            <div style={{ position: "absolute", bottom: 60, left: -20, background: "linear-gradient(135deg,#6D28D9,#8B5CF6)", borderRadius: 16, padding: "13px 18px", boxShadow: "0 12px 36px rgba(139,92,246,.4)", animation: "float 3.5s ease-in-out infinite .5s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 20, color: "#fff", lineHeight: 1 }}>480+</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 500, marginTop: 3 }}>Apps Shipped 📱</div>
            </div>
            <div style={{ position: "absolute", top: 120, right: -16, background: "#fff", borderRadius: 14, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.1)", border: "1.5px solid #D1FAE5", animation: "float 4s ease-in-out infinite 1s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,#10B981,#059669)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>✓</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 12, color: "#065F46", fontFamily: "'Sora',sans-serif" }}>App Approved!</div>
                  <div style={{ fontSize: 10, color: "#6B7280" }}>Play Store • Just now</div>
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", bottom: 160, right: -10, background: "#fff", borderRadius: 14, padding: "11px 15px", boxShadow: "0 8px 28px rgba(0,0,0,.08)", border: "1px solid #E5E7EB", animation: "float 3.8s ease-in-out infinite .3s", zIndex: 10 }}>
              <div style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 700, marginBottom: 6, textTransform: "uppercase" }}>Built With</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["💙", "⚛️", "🤖", "🍎"].map((e, i) => (
                  <span key={i} style={{ fontSize: 18 }}>{e}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Platform badges */}
        <div style={{ marginTop: 64, display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
          {PLATFORMS.map(p => (
            <div key={p.name} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 20px",
              background: p.bg, borderRadius: 14, border: `1px solid ${p.color}22`,
            }}>
              <span style={{ fontSize: 22 }}>{p.icon}</span>
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: "#0F172A" }}>{p.name}</div>
                <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500 }}>{p.count}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#0F172A", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 32, textAlign: "center" }} className="mad-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,3vw,40px)", fontWeight: 900, color: "#fff", lineHeight: 1 }}>{s.val}</div>
            <div style={{ color: "#64748B", fontSize: 13, marginTop: 6, fontWeight: 500 }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "96px 40px", background: "#fff" }} className="mad-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#8B5CF6", letterSpacing: ".1em", textTransform: "uppercase" }}>What We Build</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Mobile App Development Services</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            From native iOS to cross-platform Flutter — every app we ship is built for speed, delight, and retention.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? s.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 24px 64px ${s.color}22` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s ease",
                transform: hov === i ? "translateY(-6px)" : "none",
              }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${s.color}25` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, marginBottom: 18 }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}12`, color: s.color, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, border: `1px solid ${s.color}25` }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .2s" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const points = [
    { icon: "⚡", title: "Performance-Obsessed", desc: "Sub-second load times, smooth 60fps animations, and lean APK/IPA sizes. We profile every build with Instruments and Android Profiler before release.", color: "#8B5CF6" },
    { icon: "🎨", title: "Platform-Native UX", desc: "iOS apps feel like iOS apps. Android apps feel like Android apps. We follow HIG and Material Design 3 — not generic templates.", color: "#3B82F6" },
    { icon: "🔒", title: "Enterprise-Grade Security", desc: "Certificate pinning, biometric auth, encrypted storage, code obfuscation, and third-party pen testing for FinTech and Healthcare apps.", color: "#10B981" },
    { icon: "🧪", title: "Zero Store Rejections", desc: "Our dedicated App Store specialist knows Apple and Google review guidelines inside-out. 100% first-attempt approval rate across 480+ apps.", color: "#F59E0B" },
    { icon: "📊", title: "Data-Driven Growth", desc: "We instrument every app with Mixpanel/Amplitude from day one so you can A/B test, optimise funnels, and make product decisions from real data.", color: "#EF4444" },
    { icon: "🔄", title: "Full Lifecycle Partner", desc: "Discovery → design → build → QA → launch → growth. One team that owns every phase — no handoff headaches, no finger-pointing.", color: "#EC4899" },
  ];
  const [hov, setHov] = useState(null);

  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="mad-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#8B5CF6", letterSpacing: ".1em", textTransform: "uppercase" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>The Techvanto Difference</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Why 480+ apps and counting choose us over every other agency.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
          {points.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? p.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: 20,
              }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${p.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#0F172A", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.7 }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "96px 40px", background: "#fff" }} className="mad-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#8B5CF6", letterSpacing: ".1em", textTransform: "uppercase" }}>Tech Stack</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Technologies We Master</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Native, cross-platform, backend — we pick the best tool for your app's unique needs.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#6D28D9,#8B5CF6)" : "#fff",
              borderColor: active === i ? "#8B5CF6" : "#E5E7EB",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(139,92,246,.35)" : "none",
            }}>{t.category}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 20,
                border: `2px solid ${hov === i ? tech.color : "#F1F5F9"}`,
                background: hov === i ? `${tech.color}08` : "#FAFAFA",
                boxShadow: hov === i ? `0 12px 36px ${tech.color}28` : "0 1px 4px rgba(0,0,0,.04)",
                transition: "all .28s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-6px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}15` : "#fff", border: `1px solid ${hov === i ? tech.color + "30" : "#E5E7EB"}`, boxShadow: hov === i ? `0 4px 16px ${tech.color}25` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .28s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .28s", transform: hov === i ? "scale(1.1)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:24px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 2 }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 18px 18px", background: `linear-gradient(90deg,${tech.color},${tech.color}80)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "96px 40px", background: "linear-gradient(160deg,#F5F3FF 0%,#EFF6FF 100%)" }} className="mad-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#8B5CF6", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>From Idea to App Store</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>A transparent 6-stage process that turns your vision into an app users can't put down.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 20, padding: "32px 28px",
              border: "1px solid #E5E7EB", boxShadow: "0 4px 20px rgba(0,0,0,.05)",
              position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}12`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${step.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${step.color}20` }}>
                {step.icon}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}15`, padding: "3px 10px", borderRadius: 99 }}>STEP {step.n}</span>
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#0F172A", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75 }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}50)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "96px 40px", background: "#fff" }} className="mad-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#EF4444", letterSpacing: ".1em", textTransform: "uppercase" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Apps We're Proud Of</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Real apps, real users, real results — a glimpse into what we build.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 60 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 52, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="mad-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}25` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 360, objectFit: "cover", display: "block", transition: "transform .5s", transform: hov === cs.title ? "scale(1.04)" : "scale(1)" }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 99 }}>{cs.industry}</div>
              </div>
              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,36px)", fontWeight: 900, color: "#0F172A", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 28 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}12`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 99, border: `1px solid ${cs.color}25` }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 28, marginBottom: 32, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 26, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 4 }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 26px", borderRadius: 10, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}35`,
                  transition: "transform .2s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "none"}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#6D28D9", border: "2px solid #DDD6FE",
            padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .2s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F5F3FF"; e.currentTarget.style.borderColor = "#C4B5FD"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#DDD6FE"; }}
          >View All Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="mad-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#8B5CF6", letterSpacing: ".1em", textTransform: "uppercase" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16 }}>Everything you need to know before building your app.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 16,
              border: `2px solid ${open === i ? "#8B5CF6" : "#E5E7EB"}`,
              overflow: "hidden", transition: "border-color .25s",
              boxShadow: open === i ? "0 8px 32px rgba(139,92,246,.1)" : "none",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 28px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 700, fontSize: 15.5, color: "#0F172A", textAlign: "left", lineHeight: 1.4, flex: 1, marginRight: 16 }}>{faq.q}</span>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: open === i ? "#8B5CF6" : "#F1F5F9",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .25s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#6B7280", fontSize: 18, lineHeight: 1, transition: "transform .25s", display: "block", transform: open === i ? "rotate(45deg)" : "none" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 28px 24px", animation: "fadeIn .2s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.8, borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", platform: "", desc: "" });
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600); };

  const iStyle = (n) => ({
    width: "100%", padding: "13px 16px", borderRadius: 10, fontSize: 14,
    border: `2px solid ${focused === n ? "#8B5CF6" : "#E5E7EB"}`,
    background: focused === n ? "#F5F3FF" : "#FAFAFA",
    color: "#0F172A", outline: "none", fontFamily: "'Sora',sans-serif",
    transition: "all .2s", boxShadow: focused === n ? "0 0 0 4px rgba(139,92,246,.1)" : "none",
  });

  return (
    <section id="contact" style={{ padding: "96px 40px", background: "linear-gradient(160deg,#F8FAFC,#F5F3FF)" }} className="mad-section">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#8B5CF6", letterSpacing: ".1em", textTransform: "uppercase" }}>Get Started</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 14 }}>Let's Build Your App</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.75 }}>Share your app idea and get a free consultation + detailed quote within 24 hours.</p>
        </div>
        <div className="mad-contact-grid">
          {/* Info panel */}
          <div>
            <div style={{ background: "linear-gradient(135deg,#6D28D9,#7C3AED,#8B5CF6)", borderRadius: 24, padding: "40px 36px", marginBottom: 24, boxShadow: "0 20px 60px rgba(139,92,246,.3)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 8, position: "relative" }}>Quick Response Guaranteed</h3>
              <p style={{ color: "rgba(255,255,255,.75)", fontSize: 14, lineHeight: 1.7, marginBottom: 32, position: "relative" }}>We respond to every inquiry within 24 hours with a tailored mobile app proposal.</p>
              {[
                { icon: "📞", label: "Phone", val: "+91 96462 06032" },
                { icon: "✉️", label: "Email", val: "info@techvanto.com" },
                { icon: "📍", label: "Location", val: "New Delhi, India 🇮🇳" },
                { icon: "⏰", label: "Hours", val: "Mon–Sat, 9AM – 7PM IST" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600 }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 20, padding: "28px", border: "1px solid #E5E7EB", boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0F172A", marginBottom: 18 }}>What You Get — Free</h4>
              {[
                ["🎯", "App concept & feature scope review"],
                ["💰", "Transparent fixed-price quote"],
                ["🔒", "NDA signed before any discussion"],
                ["📱", "Platform recommendation (iOS / Android / Both)"],
                ["🗓️", "Detailed timeline with milestones"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 24, padding: "44px", boxShadow: "0 8px 48px rgba(0,0,0,.08)", border: "1px solid #F1F5F9" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: "linear-gradient(135deg,#8B5CF6,#6D28D9)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 40, boxShadow: "0 12px 36px rgba(139,92,246,.35)" }}>📱</div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: "#0F172A", marginBottom: 12 }}>Request Received! 🎉</h3>
                <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.7, maxWidth: 320, margin: "0 auto 28px" }}>Our mobile app team will review your idea and reach out within 24 hours.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", platform: "", desc: "" }); }} style={{ background: "linear-gradient(135deg,#6D28D9,#8B5CF6)", color: "#fff", border: "none", padding: "13px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Sora',sans-serif" }}>Submit Another Request</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#0F172A", marginBottom: 6 }}>Tell Us About Your App Idea</h3>
                <p style={{ color: "#9CA3AF", fontSize: 13.5, marginBottom: 32 }}>Fill out the form and we'll create a custom app development proposal.</p>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="mad-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" required style={iStyle("name")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="name@company.com" required style={iStyle("email")} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="mad-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Phone *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="+91 XXXXX XXXXX" required style={iStyle("phone")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Target Platform</label>
                      <select name="platform" value={form.platform} onChange={handleChange} onFocus={() => setFocused("platform")} onBlur={() => setFocused(null)} style={{ ...iStyle("platform"), appearance: "none", cursor: "pointer" }}>
                        <option value="">Select platform…</option>
                        {["iOS Only", "Android Only", "Both iOS & Android", "Cross-Platform (Flutter)", "Cross-Platform (React Native)", "Not sure yet"].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>App Description *</label>
                    <textarea name="desc" value={form.desc} onChange={handleChange} onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)} placeholder="Describe your app idea — the problem it solves, who will use it, key features you have in mind, and any existing apps that inspire you…" rows={5} required style={{ ...iStyle("desc"), resize: "vertical", minHeight: 130, lineHeight: 1.7 }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>🔒 Your idea is safe with us. We sign NDAs and never share your information.</p>
                  <button type="submit" disabled={loading} style={{
                    width: "100%", padding: "15px", borderRadius: 12,
                    background: loading ? "#C4B5FD" : "linear-gradient(135deg,#6D28D9,#8B5CF6)",
                    color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 800, fontSize: 16, fontFamily: "'Sora',sans-serif",
                    boxShadow: loading ? "none" : "0 8px 24px rgba(139,92,246,.4)",
                    transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  }}>
                    {loading ? "Submitting…" : "📱 Get Free App Consultation"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer (same as Portfolio & WebDevelopment) ───────────────────────────────
function Footer() {
  const cols = [
    { title: "Services", links: ["Web Development", "Mobile Apps", "AI & ML", "Cloud & DevOps", "UI/UX Design", "E-Commerce"] },
    { title: "Company", links: ["About Us", "Portfolio", "Blog", "Careers", "Contact", "Privacy Policy"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Laravel", "AWS & GCP", "Docker & K8s", "Python & AI"] },
  ];
  return (
    <footer style={{ background: "#0F172A", color: "#fff", padding: "72px 40px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ marginBottom: 56 }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: 140, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.75, fontSize: 14, maxWidth: 300, marginBottom: 24 }}>
              Building world-class digital products for businesses across 20+ countries. Innovation. Scale. Excellence.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#8B5CF6"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, letterSpacing: ".04em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s" }}
                  onMouseEnter={e => e.target.style.color = "#C4B5FD"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid #1E293B", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13 }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13 }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function MobileAppDevelopment() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:.4; } }

        .nav-links       { display:flex; align-items:center; gap:4px; }
        .nav-cta         { display:flex !important; }
        .mob-menu-btn    { display:none; }

        .mad-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .mad-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; }
        .mad-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .mad-contact-grid { display:grid; grid-template-columns:1fr 1.5fr; gap:48px; align-items:start; }
        .footer-grid      { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }
        .mad-form-row     { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        @media (max-width:1100px) {
          .mad-hero-grid  { gap:40px; }
          .footer-grid    { grid-template-columns:1fr 1fr; gap:32px; }
          .mad-stats-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:900px) {
          .mad-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .mad-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .mad-contact-grid { grid-template-columns:1fr; gap:32px; }
          .footer-grid      { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links        { display:none !important; }
          .nav-cta          { display:none !important; }
          .mob-menu-btn     { display:flex !important; }
        }
        @media (max-width:640px) {
          .footer-grid    { grid-template-columns:1fr; }
          .mad-stats-grid { grid-template-columns:repeat(2,1fr); gap:24px; }
          .mad-form-row   { grid-template-columns:1fr !important; }
        }
        @media (max-width:500px) { .topbar-right { display:none !important; } }
        @media (max-width:900px) { .mad-section { padding-left:20px !important; padding-right:20px !important; } }
        @media (max-width:640px) { .mad-section { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; } }
      `}</style>

      <Hero />
      <StatsBar />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}